import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetNotificationsQuery, useViewNotificationMutation } from 'store/gamesApi';
import { useAppDispatch } from 'store/hooks';
import { decrementUnreadNotificationCount } from 'store/authSlice';
import { formatLocalTime } from 'utils/formatDate';
import Pagination from '../pagination/pagination';
import Skeleton from '../skeleton/Skeleton';
import cx from 'classnames';
import SpinDetails from './spinDetails';
import FreeChipDetails from './freeChipDetails';
import BonusDetails from './bonusDetails';
import TipReceivedDetails from './tipReceivedDetails';
import LevelBonusDetails from './levelBonusDetails';
import LevelUpDetails from './levelUpDetails';
import DepositMatchCreditedDetails from './depositMatchCreditedDetails';
import styles from './notifications.module.scss';

interface NotificationsProps {
  onClose?: () => void;
}

const INTERACTIVE_TYPES = [
  'free_spin_granted',
  'free_chip_granted',
  'bonus_granted',
  'tip_received',
  'level_bonus_available',
  'level_up',
  'site_wheel_deposit_match_credited',
];

const Notifications: React.FC<NotificationsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const limit = 5;

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useGetNotificationsQuery({ page, limit }, { refetchOnMountOrArgChange: true });

  const [viewNotification] = useViewNotificationMutation();
  const [selectedSpinNotification, setSelectedSpinNotification] = useState<any>(null);
  const [loadingSpinNotificationId, setLoadingSpinNotificationId] = useState<number | null>(null);
  const [detailsCache, setDetailsCache] = useState<Record<number, any>>({});
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const items = response?.data?.docs || [];
  const totalPages = Math.ceil((response?.data?.total || 0) / limit) || 1;

  const handleNotificationClick = async (item: any) => {
    if (INTERACTIVE_TYPES.includes(item.type)) {
      if (detailsCache[item.id]) {
        setIsFadingOut(true);
        setTimeout(() => {
          setSelectedSpinNotification(detailsCache[item.id]);
          setSelectedTitle(item.title);
          setSelectedMessage(item.message);
          setSelectedType(item.type);
          setSelectedNotificationId(item.id);
          setIsFadingOut(false);
        }, 200);
        return;
      }

      setIsFadingOut(true);

      const fetchPromise = viewNotification(item.id)
        .unwrap()
        .catch((err) => {
          console.error('Failed to view notification', err);
          return null;
        });

      const timerPromise = new Promise((resolve) => setTimeout(resolve, 200));

      setSelectedTitle(item.title);
      setSelectedMessage(item.message);
      setSelectedType(item.type);
      setSelectedNotificationId(item.id);

      // Wait for the animation 200ms specifically
      await timerPromise;
      setIsFadingOut(false);
      setLoadingSpinNotificationId(item.id);

      // Now wait for API (if it hasn't finished already)
      const res = await fetchPromise;

      if (res) {
        let detailedData = res.data?.data;
        if (!detailedData && res.data?.payload) {
          detailedData = { ...res.data.payload };
        }
        if (detailedData) {
          detailedData = {
            ...detailedData,
            createdAt: detailedData.createdAt || res.data?.createdAt || item.createdAt,
            message: detailedData.message || res.data?.message || item.message,
          };
        }
        if (!item.isViewed) {
          refetch(); // Refresh list to update unread status and count
          dispatch(decrementUnreadNotificationCount());
        }
        if (detailedData) {
          setDetailsCache((prev) => ({ ...prev, [item.id]: detailedData }));
          setSelectedSpinNotification(detailedData);
        } else {
          setLoadingSpinNotificationId(null);
        }
      } else {
        setLoadingSpinNotificationId(null);
      }
    }
  };

  if (selectedSpinNotification || loadingSpinNotificationId) {
    const handleBack = () => {
      setSelectedSpinNotification(null);
      setLoadingSpinNotificationId(null);
      setSelectedTitle(null);
      setSelectedMessage(null);
      setSelectedType(null);
      setSelectedNotificationId(null);
    };

    const detailsProps = {
      details: selectedSpinNotification,
      title: selectedTitle,
      message: selectedMessage,
      isLoading: !selectedSpinNotification,
      onBack: handleBack,
      onPlayClick: onClose,
      notificationId: selectedNotificationId,
    };

    const ComponentMap: Record<string, React.FC<any>> = {
      tip_received: TipReceivedDetails,
      bonus_granted: BonusDetails,
      free_chip_granted: FreeChipDetails,
      level_bonus_available: LevelBonusDetails,
      level_up: LevelUpDetails,
      site_wheel_deposit_match_credited: DepositMatchCreditedDetails,
    };

    const DetailComponent = selectedType ? ComponentMap[selectedType] || SpinDetails : SpinDetails;

    return <DetailComponent {...detailsProps} />;
  }

  return (
    <div className={cx(styles.container, { [styles.fadingOut]: isFadingOut })}>
      {isLoading || isFetching ? (
        <div className={styles.list}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.notificationCard}>
              <div className={styles.headerRow}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="60px" height={16} />
              </div>
              <Skeleton width="100%" height={16} style={{ marginTop: '8px' }} />
              <Skeleton width="80%" height={16} />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          {t('notifications.empty', 'You have no notifications yet.')}
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((item: any) => (
            <div
              key={item.id}
              className={cx(styles.notificationCard, {
                [styles.unread]: !item.isViewed,
                [styles.clickable]: INTERACTIVE_TYPES.includes(item.type),
              })}
              onClick={() => handleNotificationClick(item)}
            >
              <div className={styles.headerRow}>
                <h3 className={styles.notificationTitle}>{item.title}</h3>
                <span className={styles.time}>{formatLocalTime(item.createdAt)}</span>
              </div>
              <p className={styles.message}>{item.message}</p>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isFetching && items.length > 0 && totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default Notifications;
